import { useCallback, useEffect, useState } from "react";
import { PageHeader } from "../components/Layout";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Trash2, Filter } from "lucide-react";
import { toast } from "sonner";

const SOURCES = ["dashboard", "streamdeck", "twitch", "api", "test"];
const TYPES = ["WHEEL_SPIN", "OBJECTIVE_DRAW", "SUB"];

const SOURCE_COLOR = {
  dashboard: "#f59e0b", streamdeck: "#0ea5e9", twitch: "#a855f7", api: "#94a3b8", test: "#64748b",
};

export default function HistoryPage() {
  const [rows, setRows] = useState([]);
  const [source, setSource] = useState("all");
  const [eventType, setEventType] = useState("all");

  const load = useCallback(async () => {
    const params = {};
    if (source !== "all") params.source = source;
    if (eventType !== "all") params.eventType = eventType;
    setRows(await api.history(params));
  }, [source, eventType]);
  useEffect(() => { load(); }, [load]);

  const clear = async () => {
    if (!window.confirm("Cancellare tutta la cronologia?")) return;
    await api.clearHistory();
    setRows([]);
    toast.success("Cronologia cancellata");
  };

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString("it-IT"); } catch { return iso; }
  };

  return (
    <div className="p-5 md:p-8 max-w-5xl mx-auto">
      <PageHeader
        title="Cronologia"
        subtitle="Registro di tutti gli eventi con origine, risultato e orario."
        right={
          <Button onClick={clear} variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10" data-testid="clear-history-button">
            <Trash2 className="w-4 h-4 mr-2" /> Cancella cronologia
          </Button>
        }
      />

      <div className="flex flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-500" />
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger className="w-40 bg-[#0b0e16] border-[#334155] text-white" data-testid="filter-source"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le origini</SelectItem>
              {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Select value={eventType} onValueChange={setEventType}>
          <SelectTrigger className="w-44 bg-[#0b0e16] border-[#334155] text-white" data-testid="filter-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i tipi</SelectItem>
            {TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2" data-testid="history-list">
        {rows.map((r) => (
          <div key={r.id} className="flex items-center gap-3 bg-[#131722] border border-[#334155] rounded-lg px-4 py-3">
            <span className="font-mono text-xs text-slate-500 w-36 shrink-0">{fmt(r.timestamp)}</span>
            <span
              className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded shrink-0"
              style={{ backgroundColor: `${SOURCE_COLOR[r.source] || "#64748b"}22`, color: SOURCE_COLOR[r.source] || "#94a3b8" }}
            >
              {r.source}
            </span>
            {r.isTest && <span className="text-[10px] font-bold uppercase bg-slate-600/30 text-slate-300 px-2 py-1 rounded shrink-0">TEST</span>}
            <span className="text-slate-400 text-sm hidden sm:inline shrink-0">{r.wheel || r.eventType}</span>
            <span className="text-white font-semibold truncate">→ {r.username ? `${r.username} · ` : ""}{r.result}</span>
          </div>
        ))}
        {rows.length === 0 && <div className="text-slate-500 text-sm py-10 text-center">Nessun evento registrato.</div>}
      </div>
    </div>
  );
}
